import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { PaymentButton } from '@/components/PaymentButton';
import {
  BookOpen, FileText, Clock, CheckCircle, XCircle, AlertCircle, MessageSquare,
  Award, Send, Download, RefreshCw, Calendar, AlertTriangle, BarChart2,
  Edit, ArrowLeft, CreditCard, Building2, Upload
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CurriculumStatsCards,
  CurriculumOverviewTab,
  CurriculumCertificatesTab,
  CurriculumUpdatesTab,
  CurriculumRenewalsTab
} from '@/components/curriculum';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fee constants
const CURRICULUM_APPLICATION_FEE = 30000;
const CURRICULUM_RENEWAL_FEE = 20000;

export default function CurriculumDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [validityAlerts, setValidityAlerts] = useState(null);
  const [updateRequests, setUpdateRequests] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [selectedCertId, setSelectedCertId] = useState(null);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [showBankTransferModal, setShowBankTransferModal] = useState(null);
  const [bankTransferForm, setBankTransferForm] = useState({
    transfer_reference: '',
    transfer_date: '',
    bank_name: '',
    remarks: ''
  });
  const [updateForm, setUpdateForm] = useState({
    update_type: '',
    description: '',
    changes_summary: '',
    effective_date: '',
    document_file: null
  });
  const [uploadingUpdate, setUploadingUpdate] = useState(false);

  useEffect(() => {
    if (token) {
      fetchAll();
    }
  }, [token]);

  const fetchAll = async () => {
    try {
      const [dashRes, certsRes, alertsRes, updatesRes, renewalsRes] = await Promise.all([
        axios.get(`${API}/curriculum/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/curriculum/certificates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/curriculum/validity-alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/curriculum/update-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/curriculum/renewals`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDashboard(dashRes.data);
      setCertificates(certsRes.data);
      setValidityAlerts(alertsRes.data);
      setUpdateRequests(updatesRes.data);
      setRenewals(renewalsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async (certId) => {
    try {
      const response = await axios.get(`${API}/curriculum/certificate/${certId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IMCAC_Curriculum_Certificate.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Certificate downloaded');
    } catch (err) {
      toast.error('Failed to download certificate');
    }
  };

  const submitUpdate = async (e) => {
    e.preventDefault();
    if (!selectedCertId) return;
    if (!updateForm.update_type) {
      toast.error('Please select an update type');
      return;
    }
    
    setUploadingUpdate(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('update_type', updateForm.update_type);
      formData.append('description', updateForm.description);
      formData.append('changes_summary', updateForm.changes_summary);
      formData.append('effective_date', updateForm.effective_date || '');
      
      if (updateForm.document_file) {
        formData.append('document', updateForm.document_file);
      }
      
      await axios.post(`${API}/curriculum/update-request?cert_id=${selectedCertId}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Curriculum update submitted successfully!');
      setShowUpdateDialog(false);
      setUpdateForm({ update_type: '', description: '', changes_summary: '', effective_date: '', document_file: null });
      fetchAll();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Failed to submit update');
      }
    } finally {
      setUploadingUpdate(false);
    }
  };

  const submitRenewal = async (certId) => {
    try {
      await axios.post(`${API}/curriculum/renewal-request/${certId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Renewal request submitted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit renewal');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.subject || !messageForm.message) {
      toast.error('Please fill all fields');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/portal/messages`, {
        ...messageForm,
        portal_type: 'curriculum'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message sent to IMCAC');
      setShowMessageForm(false);
      setMessageForm({ subject: '', message: '' });
      fetchAll();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const submitBankTransfer = async (applicationId, feeType) => {
    if (!bankTransferForm.transfer_reference || !bankTransferForm.transfer_date || !bankTransferForm.bank_name) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API}/curriculum/bank-transfer`, {
        application_id: applicationId,
        fee_type: feeType,
        ...bankTransferForm,
        amount: feeType === 'renewal' ? CURRICULUM_RENEWAL_FEE : CURRICULUM_APPLICATION_FEE
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bank transfer details submitted. Awaiting admin verification.');
      setShowBankTransferModal(null);
      setBankTransferForm({ transfer_reference: '', transfer_date: '', bank_name: '', remarks: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bank transfer');
    }
  };

  const downloadInvoice = async (applicationId) => {
    try {
      const response = await axios.get(`${API}/curriculum/invoice/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IMCAC_Curriculum_Invoice_${applicationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded');
    } catch (err) {
      toast.error('Failed to download invoice');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { bg: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Submitted' },
      in_review: { bg: 'bg-amber-100 text-amber-700', icon: Clock, label: 'In Review' },
      under_review: { bg: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Under Review' },
      approved: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
      rejected: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' },
      pending: { bg: 'bg-slate-100 text-slate-700', icon: Clock, label: 'Pending' },
      valid: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Valid' },
      expired: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: 'Expired' }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} rounded-sm px-2 py-0.5`}>
        <Icon className="h-3 w-3 mr-1" /> {style.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  }

  const stats = dashboard?.stats || {};
  const applications = dashboard?.applications || [];
  const messages = dashboard?.messages || [];

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="curriculum-dashboard">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-10">
        <div className="imcac-container">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="text-slate-300 hover:text-white mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Curriculum Portal</p>
              <h1 className="text-3xl font-bold text-white font-heading">Welcome, {user?.full_name}</h1>
              <p className="text-sm text-slate-300 mt-1">Manage curriculum approvals, certificates, and updates</p>
            </div>
            <Button asChild className="bg-[#C5A572] text-white rounded-sm">
              <a href="/apply/curriculum">
                <FileText className="h-4 w-4 mr-1" /> New Submission
              </a>
            </Button>
          </div>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Validity Alerts Banner */}
        {validityAlerts && validityAlerts.critical_count > 0 && (
          <Alert className="mb-6 border-red-500 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertTitle className="text-red-700">Certificate Attention Required</AlertTitle>
            <AlertDescription className="text-red-600">
              {validityAlerts.critical_count} certificate(s) require immediate attention. 
              Please review the Certificates tab.
            </AlertDescription>
          </Alert>
        )}

        <CurriculumStatsCards stats={stats} />

        {/* Main Tabs */}
        <Card className="imcac-card">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start h-auto flex-wrap bg-[#F8FAFC] rounded-none border-b border-slate-200 p-1 gap-1">
              <TabsTrigger value="overview" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <BarChart2 className="h-4 w-4 mr-1" /> Overview
              </TabsTrigger>
              <TabsTrigger value="applications" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-applications">
                <FileText className="h-4 w-4 mr-1" /> Applications
              </TabsTrigger>
              <TabsTrigger value="certificates" className="rounded-sm text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white" data-testid="tab-certificates">
                <Award className="h-4 w-4 mr-1" /> Certificates
              </TabsTrigger>
              <TabsTrigger value="updates" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-updates">
                <Edit className="h-4 w-4 mr-1" /> Updates
              </TabsTrigger>
              <TabsTrigger value="renewals" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-renewals">
                <RefreshCw className="h-4 w-4 mr-1" /> Renewals
              </TabsTrigger>
              <TabsTrigger value="messages" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <MessageSquare className="h-4 w-4 mr-1" /> Messages
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="p-5">
              <CurriculumOverviewTab
                validityAlerts={validityAlerts}
                certificates={certificates}
                applications={applications}
                onDownload={downloadCertificate}
                onShowUpdate={(certId) => { setSelectedCertId(certId); setShowUpdateDialog(true); }}
              />
            </TabsContent>

            {/* Applications Tab */}
            <TabsContent value="applications" className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#1B2A4A]">Your Applications</h3>
                <Button asChild className="bg-[#1B2A4A] text-white rounded-sm text-sm">
                  <a href="/apply/curriculum">
                    <FileText className="h-4 w-4 mr-1" /> New Submission
                  </a>
                </Button>
              </div>

              {applications.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No applications found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {applications.map(app => (
                    <Card key={app.id} className="hover:border-[#C5A572] transition-colors">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-[#1B2A4A]">{app.curriculum_title || 'Curriculum Submission'}</h4>
                              {getStatusBadge(app.status)}
                              {app.payment_status === 'completed' && (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <CheckCircle className="h-3 w-3 mr-1" /> Paid
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600">{app.institution_affiliation}</p>
                            <p className="text-xs text-slate-500 mt-1">Specialty: {app.specialty}</p>
                            <p className="text-xs text-slate-500">Application Fee: ₹{CURRICULUM_APPLICATION_FEE.toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Submitted</p>
                            <p className="text-sm text-slate-700">{new Date(app.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        
                        {/* Payment Section - Show when approved by admin and payment pending */}
                        {app.status === 'approved' && app.payment_status !== 'completed' && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="p-3 bg-amber-50 rounded-sm border border-amber-200 mb-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-medium text-amber-800">
                                    <AlertCircle className="h-4 w-4 inline mr-1" /> Payment Required
                                  </p>
                                  <p className="text-xs text-amber-700">Complete payment to finalize curriculum approval</p>
                                </div>
                                <p className="text-lg font-bold text-amber-700">₹{CURRICULUM_APPLICATION_FEE.toLocaleString()}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <PaymentButton
                                paymentType="curriculum_submission"
                                amount={CURRICULUM_APPLICATION_FEE}
                                referenceId={app.id}
                                description={`Curriculum Application Fee - ${app.curriculum_title}`}
                                onSuccess={() => {
                                  toast.success('Payment successful! Curriculum approved.');
                                  fetchAll();
                                }}
                                onError={(error) => toast.error(error || 'Payment failed')}
                                className="flex-1 bg-[#C5A572] hover:bg-[#B8956A] text-white rounded-sm"
                              >
                                <CreditCard className="h-4 w-4 mr-2" /> Pay Now
                              </PaymentButton>
                              <Button 
                                variant="outline" 
                                className="flex-1 rounded-sm border-[#1B2A4A] text-[#1B2A4A]"
                                onClick={() => setShowBankTransferModal({ id: app.id, type: 'application', title: app.curriculum_title })}
                              >
                                <Building2 className="h-4 w-4 mr-2" /> Bank Transfer
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Payment Completed - Show invoice download */}
                        {app.payment_status === 'completed' && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center justify-between p-3 bg-green-50 rounded-sm border border-green-200">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-green-600" />
                                <div>
                                  <p className="text-sm font-medium text-green-800">Payment Completed</p>
                                  <p className="text-xs text-green-700">Curriculum is now fully approved</p>
                                </div>
                              </div>
                              <Button 
                                size="sm"
                                variant="outline"
                                className="rounded-sm text-green-700 border-green-300"
                                onClick={() => downloadInvoice(app.id)}
                              >
                                <Download className="h-4 w-4 mr-1" /> Invoice
                              </Button>
                            </div>
                          </div>
                        )}

                        {app.rejection_reason && (
                          <div className="mt-3 pt-3 border-t bg-red-50 p-2 rounded-sm">
                            <p className="text-xs text-red-600"><strong>Reason:</strong> {app.rejection_reason}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Certificates Tab */}
            <TabsContent value="certificates" className="p-5" data-testid="certificates-content">
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Your Certificates</h3>
              <CurriculumCertificatesTab
                certificates={certificates}
                onDownload={downloadCertificate}
                onShowUpdate={(certId) => { setSelectedCertId(certId); setShowUpdateDialog(true); }}
              />
            </TabsContent>

            {/* Updates Tab */}
            <TabsContent value="updates" className="p-5" data-testid="updates-content">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#1B2A4A]">Curriculum Updates</h3>
                {(certificates.length > 0 || applications.some(app => app.status === 'approved' || app.status === 'certified')) && (
                  <Button 
                    onClick={() => { 
                      const certId = certificates[0]?.id || applications.find(app => app.status === 'approved' || app.status === 'certified')?.id;
                      setSelectedCertId(certId); 
                      setShowUpdateDialog(true); 
                    }}
                    className="bg-[#1B2A4A] text-white rounded-sm text-sm"
                    data-testid="upload-update-btn"
                  >
                    <Upload className="h-4 w-4 mr-1" /> Submit Curriculum Update
                  </Button>
                )}
              </div>

              {/* Show message if no approved application */}
              {certificates.length === 0 && !applications.some(app => app.status === 'approved' || app.status === 'certified') && (
                <Card className="mb-6 bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-800 mb-1">Curriculum Updates Not Available</h4>
                        <p className="text-sm text-amber-700">
                          You need an approved curriculum application before you can submit updates. 
                          Please submit a new curriculum application from the Applications tab first.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Update Policy Info */}
              <Card className="mb-6 bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" /> Free Update Policy
                  </h4>
                  <ul className="text-xs text-green-700 space-y-1 list-disc list-inside">
                    <li>You are allowed <strong>2 free curriculum updates per year</strong></li>
                    <li>Updates used this year: <strong>{dashboard?.updates_used_this_year || 0}/2</strong></li>
                    <li>Additional updates may require admin approval</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="mb-6 bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Update Types</h4>
                  <div className="grid md:grid-cols-3 gap-3 text-xs text-blue-700">
                    <div className="p-2 bg-white rounded-sm">
                      <strong>Minor Revision:</strong> Small corrections, formatting changes, typo fixes
                    </div>
                    <div className="p-2 bg-white rounded-sm">
                      <strong>Major Revision:</strong> Content changes, new modules, structural updates
                    </div>
                    <div className="p-2 bg-white rounded-sm">
                      <strong>Annual Update:</strong> Regular yearly updates, compliance updates
                    </div>
                  </div>
                </CardContent>
              </Card>

              <CurriculumUpdatesTab updateRequests={updateRequests} />
            </TabsContent>

            {/* Renewals Tab */}
            <TabsContent value="renewals" className="p-5" data-testid="renewals-content">
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Renewal Requests</h3>

              <Card className="mb-6 bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <h4 className="font-medium text-amber-800 mb-2">Renewal Information</h4>
                  <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
                    <li>Curriculum accreditation validity: <strong>3 years</strong></li>
                    <li>Free updation in curriculum allowed: <strong>2 times a year</strong></li>
                    <li>Renewals can be requested within <strong>6 months of expiry</strong></li>
                    <li>Renewal fee: <strong>₹{CURRICULUM_RENEWAL_FEE.toLocaleString()}</strong></li>
                    <li>Application fee: <strong>₹{CURRICULUM_APPLICATION_FEE.toLocaleString()}</strong></li>
                    <li>Review process takes <strong>2-4 weeks</strong></li>
                  </ul>
                </CardContent>
              </Card>

              <CurriculumRenewalsTab
                renewals={renewals}
                onPayment={(renewalId, paymentId) => {
                  toast.success('Payment successful!');
                  fetchAll();
                }}
                onShowBankTransfer={(renewal) => setShowBankTransferModal({ id: renewal.id, type: 'renewal', title: renewal.curriculum_title })}
              />
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#1B2A4A]">Messages</h3>
                <Button onClick={() => setShowMessageForm(!showMessageForm)} className="bg-[#C5A572] text-white rounded-sm text-sm">
                  <Send className="h-4 w-4 mr-1" /> New Message
                </Button>
              </div>

              {showMessageForm && (
                <Card className="mb-4 border-2 border-[#C5A572]">
                  <CardContent className="p-4">
                    <form onSubmit={sendMessage} className="space-y-3">
                      <div>
                        <Label className="text-xs">Subject</Label>
                        <Input
                          value={messageForm.subject}
                          onChange={e => setMessageForm({...messageForm, subject: e.target.value})}
                          placeholder="Message subject"
                          className="mt-1 rounded-sm"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Message</Label>
                        <textarea
                          value={messageForm.message}
                          onChange={e => setMessageForm({...messageForm, message: e.target.value})}
                          placeholder="Write your message to IMCAC..."
                          className="w-full mt-1 p-2 border border-slate-200 rounded-sm text-sm"
                          rows={4}
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="bg-[#1B2A4A] text-white rounded-sm text-sm" disabled={sending}>
                          {sending ? 'Sending...' : 'Send Message'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowMessageForm(false)} className="rounded-sm text-sm">Cancel</Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {messages.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No messages yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`border rounded-sm p-4 ${msg.direction === 'outgoing' ? 'border-[#C5A572] bg-[#C5A572]/5' : 'border-slate-200'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className={`text-xs mb-1 ${msg.direction === 'outgoing' ? 'bg-[#C5A572] text-white' : 'bg-blue-100 text-blue-700'}`}>
                            {msg.direction === 'outgoing' ? 'From IMCAC' : 'Sent'}
                          </Badge>
                          <h4 className="text-sm font-semibold text-[#1B2A4A]">{msg.subject}</h4>
                          <p className="text-sm text-slate-600 mt-1">{msg.message}</p>
                        </div>
                        <span className="text-xs text-slate-400">{new Date(msg.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>

        {/* Quick Links */}
        <Card className="imcac-card mt-6">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3">Quick Links</h3>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm" className="rounded-sm text-xs">
                <a href="/apply/curriculum">Submit Curriculum</a>
              </Button>
              <Button asChild variant="outline" size="sm" className="rounded-sm text-xs">
                <a href="/curriculum">Curriculum Guidelines</a>
              </Button>
              <Button asChild variant="outline" size="sm" className="rounded-sm text-xs">
                <a href="/contact">Contact IMCAC</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Update Request Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#C5A572]" /> Submit Curriculum Update
            </DialogTitle>
          </DialogHeader>
          
          {/* Free updates info */}
          <div className="p-3 bg-green-50 border border-green-200 rounded-sm mb-2">
            <p className="text-xs text-green-700">
              <CheckCircle className="h-3 w-3 inline mr-1" />
              Free updates remaining this year: <strong>{2 - (dashboard?.updates_used_this_year || 0)}/2</strong>
            </p>
          </div>
          
          <form onSubmit={submitUpdate} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Update Type *</Label>
              <Select value={updateForm.update_type} onValueChange={v => setUpdateForm({...updateForm, update_type: v})}>
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue placeholder="Select update type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minor_revision">Minor Revision - Small corrections, formatting</SelectItem>
                  <SelectItem value="major_revision">Major Revision - Content changes, new modules</SelectItem>
                  <SelectItem value="annual_update">Annual Update - Regular yearly updates</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Update Title / Description *</Label>
              <Input 
                value={updateForm.description}
                onChange={e => setUpdateForm({...updateForm, description: e.target.value})}
                className="mt-1 rounded-sm"
                placeholder="e.g., Updated Module 3 - Patient Safety Protocols"
                required
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Changes Summary *</Label>
              <Textarea 
                value={updateForm.changes_summary}
                onChange={e => setUpdateForm({...updateForm, changes_summary: e.target.value})}
                className="mt-1 rounded-sm"
                rows={3}
                placeholder="Describe all changes made in this update..."
                required
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Effective Date</Label>
              <Input 
                type="date"
                value={updateForm.effective_date}
                onChange={e => setUpdateForm({...updateForm, effective_date: e.target.value})}
                className="mt-1 rounded-sm"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Upload Updated Curriculum Document</Label>
              <div className="mt-1 border-2 border-dashed border-slate-300 rounded-sm p-4 text-center hover:border-[#C5A572] transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={e => setUpdateForm({...updateForm, document_file: e.target.files[0]})}
                  className="hidden"
                  id="curriculum-file-upload"
                />
                <label htmlFor="curriculum-file-upload" className="cursor-pointer">
                  {updateForm.document_file ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <FileText className="h-5 w-5" />
                      <span className="text-sm font-medium">{updateForm.document_file.name}</span>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">Click to upload curriculum document</p>
                      <p className="text-xs text-slate-400 mt-1">PDF, DOC, DOCX (Max 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowUpdateDialog(false);
                  setUpdateForm({ update_type: '', description: '', changes_summary: '', effective_date: '', document_file: null });
                }} 
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1B2A4A] text-white rounded-sm"
                disabled={uploadingUpdate}
              >
                {uploadingUpdate ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-1" /> Submit Update
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bank Transfer Modal */}
      {showBankTransferModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBankTransferModal(null)}>
          <Card className="max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <CardHeader className="bg-[#1B2A4A] text-white rounded-t-lg">
              <CardTitle className="text-base">Submit Bank Transfer Details</CardTitle>
              <CardDescription className="text-slate-300">
                {showBankTransferModal.type === 'renewal' ? 'Renewal Fee Payment' : 'Curriculum Application Fee'}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="p-3 bg-amber-50 rounded-sm border border-amber-200">
                  <p className="text-sm font-medium text-amber-800">{showBankTransferModal.title}</p>
                  <p className="text-lg font-bold text-amber-700">
                    ₹{(showBankTransferModal.type === 'renewal' ? CURRICULUM_RENEWAL_FEE : CURRICULUM_APPLICATION_FEE).toLocaleString()}
                  </p>
                </div>
                
                <div className="p-3 bg-slate-50 rounded-sm">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Bank Account Details</p>
                  <p className="text-sm"><span className="text-slate-500">Bank:</span> <span className="font-medium">Yes Bank</span></p>
                  <p className="text-sm"><span className="text-slate-500">Branch:</span> <span className="font-medium">Khar West</span></p>
                  <p className="text-sm"><span className="text-slate-500">Account:</span> <span className="font-medium">021188700000261</span></p>
                  <p className="text-sm"><span className="text-slate-500">IFSC:</span> <span className="font-medium">YESB0000211</span></p>
                  <p className="text-sm"><span className="text-slate-500">Name:</span> <span className="font-medium">International Medical Competency Accreditation Council</span></p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Transfer Reference / UTR Number *</Label>
                  <Input
                    value={bankTransferForm.transfer_reference}
                    onChange={e => setBankTransferForm({...bankTransferForm, transfer_reference: e.target.value})}
                    placeholder="Enter UTR/Reference number"
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Transfer Date *</Label>
                  <Input
                    type="date"
                    value={bankTransferForm.transfer_date}
                    onChange={e => setBankTransferForm({...bankTransferForm, transfer_date: e.target.value})}
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Your Bank Name *</Label>
                  <Input
                    value={bankTransferForm.bank_name}
                    onChange={e => setBankTransferForm({...bankTransferForm, bank_name: e.target.value})}
                    placeholder="Bank from which you transferred"
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Remarks (Optional)</Label>
                  <Textarea
                    value={bankTransferForm.remarks}
                    onChange={e => setBankTransferForm({...bankTransferForm, remarks: e.target.value})}
                    placeholder="Any additional notes"
                    className="rounded-sm mt-1"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button 
                  onClick={() => submitBankTransfer(showBankTransferModal.id, showBankTransferModal.type)} 
                  className="bg-[#1B2A4A] text-white rounded-sm"
                >
                  Submit Transfer Details
                </Button>
                <Button variant="outline" onClick={() => setShowBankTransferModal(null)} className="rounded-sm">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
